import React, { useState } from 'react';
import WDData from '../../Data/WDBikes.json';
import WDRecordCard from './WDRecordCard';
/* ----------------- Helper Functions (unchanged) ----------------- */

const generateNumberRange = (start, end) => {
  const range = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
};

const normaliseRange = (str) => str.replace(/–/g, "-");

const expandRanges = (arr) => {
  if (!arr || !Array.isArray(arr)) return [];
  let output = [];

  arr.forEach(item => {
    if (!item) return;
    const cleaned = normaliseRange(item.trim());

    if (/^\d+-\d+$/.test(cleaned)) {
      const [s, e] = cleaned.split("-").map(n => parseInt(n, 10));
      for (let i = s; i <= e; i++) output.push(i);
    }
    else if (/^\d+$/.test(cleaned)) output.push(parseInt(cleaned, 10));
  });

  return output;
};

// e.g. "41-G3L-23800 to 41-G3L-25049"
const expandEngineRange = (arr) => {
  if (!arr || !Array.isArray(arr)) return [];
  let output = [];

  arr.forEach(item => {
    if (!item) return;

    const cleaned = normaliseRange(item.trim());
    const m = cleaned.match(/^(.*?)(\d+)\s+to\s+(.*?)(\d+)$/);

    if (m) {
      const prefix = m[1];
      const start = parseInt(m[2], 10);
      const end = parseInt(m[4], 10);
      for (let i = start; i <= end; i++) output.push(prefix + i);
    }
    else if (/^\d+$/.test(cleaned)) output.push(cleaned);
  });

  return output;
};

const expandSerials = (arr) => {
  if (!arr || !Array.isArray(arr)) return [];
  let output = [];

  arr.forEach(item => {
    if (!item) return;
    const cleaned = normaliseRange(item.trim());

    if (/^\d+-\d+$/.test(cleaned)) {
      const [s, e] = cleaned.split("-").map(Number);
      for (let i = s; i <= e; i++) output.push(i);
    }
    else if (/^\d+$/.test(cleaned)) {
      output.push(parseInt(cleaned, 10));
    }
  });

  return output;
};

/* ----------------- Quantity summary handling ----------------- */

function expandQuantitiesForRecord(record) {
  const makerType = record.makerType || null;
  const qty = record.qty;
  const results = [];

  if (qty === null || qty === undefined) return results;

  if (typeof qty === "number") {
    results.push({ model: makerType ?? "Unknown", quantity: qty });
    return results;
  }

  if (typeof qty === "string" && qty.includes("-")) {
    const parts = qty.split(",");
    parts.forEach(p => {
      const m = p.trim().match(/^(\d+)\s*-\s*(.+)$/);
      if (!m) return;
      results.push({ model: m[2].trim(), quantity: parseInt(m[1], 10) });
    });
    return results;
  }

  return results;
}

function summariseWDBikes(data) {
  const byModel = {};
  let totalBikes = 0;

  data.forEach(record => {
    const entries = expandQuantitiesForRecord(record);
    entries.forEach(({ model, quantity }) => {
      byModel[model] = (byModel[model] || 0) + quantity;
      totalBikes += quantity;
    });
  });

  const byModelArray = Object.entries(byModel)
    .map(([model, qty]) => ({ model, quantity: qty }))
    .sort((a, b) => b.quantity - a.quantity);

  return { totalBikes, byModelArray };
}

const summary = summariseWDBikes(WDData);

/* ----------------- Tank image selector ----------------- */

const getTankImageByContractNumber = (contractNumber, makersType) => {
  switch (contractNumber) {
    case "C4608":
    case "C5247":
    case "C6094":
      return "tank1.png";
    case "C7183":
    case "C8078":
    case "C8934":
      return "tank2.png";
    case "C1050":
    case "C2604":
    case "C4555":
      return "tank4.png";
    case "C6150":
      return "tank5.png"; 
    case "C7641":
      return "tank1.png";
    default:
      return makersType.includes("G3L") ? "tank3.png" : null;
  }
};

/* ----------------- Search Functions (MULTIPLE RESULTS) ----------------- */

const findRecordsByFrameNumber = (input) => {
  const frameNumber = parseInt(input, 10);
  if (isNaN(frameNumber)) return [];

  const results = [];

  WDData.forEach(record => {
    const frameList = expandRanges(record.frames);
    const engineList = expandEngineRange(record.engines);
    const serialList = expandSerials(record.serials);

    const index = frameList.indexOf(frameNumber);
    if (index === -1) return;

    results.push({
      Model: record.makerType,
      "Military Class": record.militaryClass,
      "WD Serial No.": serialList[index] ?? null,
      "Engine No.": engineList[index] ?? null,
      "Contract Number": record.contract,
      Dates: record.dates,
      Price: record.price,
      "Delivery Destination": record.delivery,
      Tank: getTankImageByContractNumber(record.contract, record.makerType),
      "Frame No.": frameNumber
    });
  });

  return results;
};

const findRecordsByEngineNumber = (engineNumber) => {
  const results = [];

  WDData.forEach(record => {
    const frameList = expandRanges(record.frames);
    const engineList = expandEngineRange(record.engines);
    const serialList = expandSerials(record.serials);

    const index = engineList.indexOf(engineNumber);
    if (index === -1) return;

    results.push({
      Model: record.makerType,
      "Military Class": record.militaryClass,
      "WD Serial No.": serialList[index] ?? null,
      "Engine No.": engineNumber,
      "Contract Number": record.contract,
      Dates: record.dates,
      Price: record.price,
      "Delivery Destination": record.delivery,
      Tank: getTankImageByContractNumber(record.contract, record.makerType),
      "Frame No.": frameList[index] ?? null
    });
  });

  return results;
};

const findRecordsBySerialNumber = (input) => {
  const serialNo = parseInt(input, 10);
  if (isNaN(serialNo)) return [];

  const results = [];

  WDData.forEach(record => {
    const frameList = expandRanges(record.frames);
    const engineList = expandEngineRange(record.engines);
    const serialList = expandSerials(record.serials);

    const index = serialList.indexOf(serialNo);
    if (index === -1) return;

    results.push({
      Model: record.makerType,
      "Military Class": record.militaryClass,
      "WD Serial No.": serialNo,
      "Engine No.": engineList[index] ?? null,
      "Contract Number": record.contract,
      Dates: record.dates,
      Price: record.price,
      "Delivery Destination": record.delivery,
      Tank: getTankImageByContractNumber(record.contract, record.makerType),
      "Frame No.": frameList[index] ?? null
    });
  });

  return results;
};

/* ----------------- Component ----------------- */

const WDBikesTab = () => {
  const [frameNumberInput, setFrameNumberInput] = useState('');
  const [engineNumberInput, setEngineNumberInput] = useState('');
  const [serialNumberInput, setSerialNumberInput] = useState('');

  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");

  /* --------- Search Handlers --------- */

  const doSearch = (finderFn, input) => {
    const results = finderFn(input);

    if (results.length === 0) {
      setSearchMessage("No matching record found.");
      setSearchResults([]);
      return;
    }

    if (results.length > 1) {
      setSearchMessage(`Multiple records found (${results.length}).`);
    } else {
      setSearchMessage("");
    }

    setSearchResults(results);
  };

  /* --------- Inline styling --------- */
  const commonInputStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#f4f4f2',
    border: '2px solid #6b6b47',
    borderRadius: '4px',
    fontFamily: '"Courier New", monospace',
    marginBottom: '10px'
  };

  const commonButtonStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#6b6b47',
    color: '#f4f4f2',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  /* ----------------- RENDER ----------------- */

  return (
    <div className="wd-bikes-container" style={{ textAlign: 'center', marginBottom: '20px' }}>

      {/* Production Summary Card */}
      <div style={{
        maxWidth: "600px",
        margin: "30px auto",
        background: "white",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        fontFamily: '"Courier New", monospace'
      }}>
        <h3 style={{
          marginTop: 0,
          color: "#6b6b47",
          borderBottom: "2px solid #6b6b47",
          paddingBottom: "6px",
          textAlign: "center"
        }}>
          Production Summary
        </h3>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "15px" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px", background: "#e7e5d0", borderBottom: "2px solid #6b6b47" }}>Model</th>
              <th style={{ textAlign: "right", padding: "8px", background: "#e7e5d0", borderBottom: "2px solid #6b6b47" }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {summary.byModelArray.map(x => (
              <tr key={x.model}>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid #ddd" }}>{x.model}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", borderBottom: "1px solid #ddd" }}>
                  {x.quantity.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ marginTop: "12px", fontWeight: "bold", textAlign: "center" }}>
          Total Bikes: {summary.totalBikes.toLocaleString()}
        </p>
      </div>

      {/* Search Section */}
      <div style={{
        background: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        maxWidth: "1000px",
        margin: "20px auto",
        fontFamily: '"Courier New", monospace',
        textAlign: "left"
      }}>
        <h2 style={{ color: '#6b6b47' }}>WD Bike Search</h2>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Frame search */}
          <div style={{ width: '100%', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Enter frame number"
              value={frameNumberInput}
              onChange={(e) => setFrameNumberInput(e.target.value)}
              style={commonInputStyle}
            />
            <button
              onClick={() => doSearch(findRecordsByFrameNumber, frameNumberInput)}
              style={commonButtonStyle}
            >
              Search by Frame
            </button>
          </div>

          {/* Engine search */}
          <div style={{ width: '100%', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Enter engine number"
              value={engineNumberInput}
              onChange={(e) => setEngineNumberInput(e.target.value)}
              style={commonInputStyle}
            />
            <button
              onClick={() => doSearch(findRecordsByEngineNumber, engineNumberInput)}
              style={commonButtonStyle}
            >
              Search by Engine
            </button>
          </div>

          {/* Serial search */}
          <div style={{ width: '100%', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Enter WD serial"
              value={serialNumberInput}
              onChange={(e) => setSerialNumberInput(e.target.value)}
              style={commonInputStyle}
            />
            <button
              onClick={() => doSearch(findRecordsBySerialNumber, serialNumberInput)}
              style={commonButtonStyle}
            >
              Search by Serial
            </button>
          </div>
        </div>
      </div>

      {/* Results Rendering */}
      <div style={{ marginTop: '20px' }}>
        {searchMessage && (
          <p style={{ color: searchResults.length > 1 ? "orange" : "red", fontWeight: "bold" }}>
            {searchMessage}
          </p>
        )}

        {searchResults.map((result, i) => (
          <WDRecordCard key={i} record={result} />
        ))}
      </div>

    </div>
  );
};

export default WDBikesTab;